import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import { useState } from "react";
import * as Yup from "yup";
import { post } from "../../../api/util";
import { format } from "date-fns";
import { toISOLocal } from "../../../util/date";
import {
  validateTilDatoEfterEllerSammeSomFraDato,
  validateTilKlDagEfterFraKlDag,
} from "../../../util/validation";

export function useOpretKursusLinje(fmuKursusKategorier: KodeListe[]) {
  const [step, setStep] = useState<
    1 | 2 | "kursuskvittering" | "linjekvittering"
  >(1);

  const defaultStartTime = new Date();
  defaultStartTime.setHours(10, 0, 0, 0);
  const defaultEndTime = new Date();
  defaultEndTime.setHours(16, 0, 0, 0);

  const step1 = useFormik<OpretKursusLinjeStep1Formik>({
    initialValues: {
      kursusOrLinje: "kursus",
      navn: "",
      hotelId: undefined,
      afbestilGebyr: 0,
      bemaerkninger: "",
      fraDato: undefined,
      kursustype: undefined,
      refaktureringsgebyr: 0,
      tilDato: undefined,
      fraKlDag: defaultStartTime,
      tilKlDag: defaultEndTime,
    },
    validationSchema: Yup.object({
      navn: Yup.string().required("Navn er påkrævet"),
      hotelId: Yup.string().required("Hotel er påkrævet"),
      kursustype: Yup.number().when("kursusOrLinje", {
        is: (val) => val === "kursus",
        then: (schema) => schema.required("Kursustype er påkrævet"),
      }),
      fraDato: Yup.date()
        .nullable()
        .when("kursusOrLinje", {
          is: (val: string) => val === "kursus",
          then: (schema) => schema.required("Fra dato er påkrævet"),
        }),
      tilDato: Yup.date()
        .nullable()
        .when("kursusOrLinje", {
          is: (val: string) => val === "kursus",
          then: (schema) =>
            schema
              .required("Til dato er påkrævet")
              .test(
                "tilDato",
                "Slutdato kan ikke være før startdato",
                function (val) {
                  const {
                    fraDato,
                  }: {
                    fraDato: Date | undefined;
                  } = this.parent;
                  return validateTilDatoEfterEllerSammeSomFraDato(fraDato, val);
                },
              ),
        }),
      fraKlDag: Yup.date()
        .nullable()
        .when("kursusOrLinje", {
          is: (val: string) => val === "kursus",
          then: (schema) => schema.required("Starttidspunkt er påkrævet"),
        }),
      tilKlDag: Yup.date()
        .nullable()
        .when("kursusOrLinje", {
          is: (val: string) => val === "kursus",
          then: (schema) =>
            schema
              .required("Sluttidspunkt er påkrævet")
              .test(
                "tilKlDag",
                "Sluttidspunkt må ikke vøre før starttidspunkt",
                function (val) {
                  const {
                    fraDato,
                    tilDato,
                    fraKlDag,
                  }: {
                    fraDato: Date | undefined;
                    tilDato: Date | undefined;
                    fraKlDag: Date | undefined;
                  } = this.parent;
                  return validateTilKlDagEfterFraKlDag(
                    fraDato,
                    tilDato,
                    fraKlDag,
                    val,
                  );
                },
              ),
        }),

      bemaerkninger: Yup.string(),
      afbestilGebyr: Yup.number(),
      refaktureringsgebyr: Yup.number().required(
        "Refaktureringsgebyr er påkrævet",
      ),
    }),
    onSubmit: (values) => {
      if (values.kursusOrLinje === "kursus") {
        opretKursusQuery.mutate(getOpretKursusDTO(values));
        setStep("kursuskvittering");
      } else {
        setStep(2);
      }
    },
  });
  const kurserValidering = Yup.object({
    navn: Yup.string().required("Navn er påkrævet"),
    fraDato: Yup.date().nullable().required("Fra dato er påkrævet"),
    tilDato: Yup.date()
      .nullable()
      .required("Til dato er påkrævet")
      .test("tilDato", "Slutdato kan ikke være før startdato", function (val) {
        const {
          fraDato,
        }: {
          fraDato: Date | undefined;
        } = this.parent;
        return validateTilDatoEfterEllerSammeSomFraDato(fraDato, val);
      }),
    fraKlDag: Yup.date().nullable().required("Starttidspunkt er påkrævet"),
    tilKlDag: Yup.date()
      .nullable()
      .required("Sluttidspunkt må ikke vøre før starttidspunkt")
      .test(
        "tilKlDag",
        "Sluttidspunkt må ikke vøre før starttidspunkt",
        function (val) {
          const {
            fraDato,
            tilDato,
            fraKlDag,
          }: {
            fraDato: Date | undefined;
            tilDato: Date | undefined;
            fraKlDag: Date | undefined;
          } = this.parent;
          return validateTilKlDagEfterFraKlDag(fraDato, tilDato, fraKlDag, val);
        },
      ),
    kursuskategori: Yup.number().test(
      "kursuskategori",
      "Kategori skal være unik",
      function (val) {
        // workaround to get to parent of parent:
        const kurser = (this as any).from[1]
          .value as OpretKursusLinjeStep2Formik["kurser"];

        const kursuskategorier = Object.values(kurser).map((val) => {
          return val.kursuskategori;
        });

        return kursuskategorier.filter((k) => k === val).length === 1;
      },
    ),
  });
  const step2 = useFormik<OpretKursusLinjeStep2Formik>({
    validationSchema: Yup.object({
      kurser: Yup.object({
        1: kurserValidering,
        2: kurserValidering,
        3: kurserValidering,
        4: kurserValidering,
        5: kurserValidering,
        6: kurserValidering,
        7: kurserValidering,
        8: kurserValidering,
      }),
    }),
    initialValues: {
      kurser: {
        1: {
          navn: `${step1.values.navn}-1`,
          kursuskategori: fmuKursusKategorier[7].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        2: {
          navn: `${step1.values.navn}-2`,
          kursuskategori: fmuKursusKategorier[1].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        3: {
          navn: `${step1.values.navn}-3`,
          kursuskategori: fmuKursusKategorier[3].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        4: {
          navn: `${step1.values.navn}-4`,
          kursuskategori: fmuKursusKategorier[2].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        5: {
          navn: `${step1.values.navn}-5`,
          kursuskategori: fmuKursusKategorier[5].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        6: {
          navn: `${step1.values.navn}-6`,
          kursuskategori: fmuKursusKategorier[0].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        7: {
          navn: `${step1.values.navn}-7`,
          kursuskategori: fmuKursusKategorier[6].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
        8: {
          navn: `${step1.values.navn}-8`,
          kursuskategori: fmuKursusKategorier[4].nr,
          fraDato: undefined,
          fraKlDag: defaultStartTime,
          tilDato: undefined,
          tilKlDag: defaultEndTime,
        },
      },
    },
    onSubmit: (values) => {
      const dto = getOpretLinjeDTO(step1.values, values);
      opretLinjeQuery.mutate(dto);
      setStep("linjekvittering");
    },
    enableReinitialize: true, // updates initialvalues when step1 changes
  });
  const opretKursusQuery = useMutation({
    mutationFn: (dto: OpretKursusDTO) => {
      return post<{ id: string; navn: string }>({
        query: "kursus",
        body: dto,
      });
    },
  });

  const opretLinjeQuery = useMutation({
    mutationFn: (dto: OpretLinjeDTO) => {
      return post<{ id: string; navn: string }>({
        query: "fmulinje",
        body: dto,
      });
    },
  });
  return {
    step1,
    step2,
    step,
    setStep,
    opretKursusQuery,
    opretLinjeQuery,
  };
}
export type OpretKursusQuery = ReturnType<
  typeof useOpretKursusLinje
>["opretKursusQuery"];
export type OpretLinjeQuery = ReturnType<
  typeof useOpretKursusLinje
>["opretLinjeQuery"];

export interface OpretKursusLinjeStep1Formik {
  kursusOrLinje: "kursus" | "linje";
  navn: string;
  kursustype: number | undefined;
  hotelId: string | undefined;
  fraDato: Date | undefined;
  fraKlDag: Date | undefined;
  tilKlDag: Date | undefined;
  tilDato: Date | undefined;
  bemaerkninger: string;
  afbestilGebyr: number;
  refaktureringsgebyr: number;
}
export interface OpretKursusLinjeStep2Formik {
  kurser: {
    1: OpretKursusLinjeStep2FormikKursus;
    2: OpretKursusLinjeStep2FormikKursus;
    3: OpretKursusLinjeStep2FormikKursus;
    4: OpretKursusLinjeStep2FormikKursus;
    5: OpretKursusLinjeStep2FormikKursus;
    6: OpretKursusLinjeStep2FormikKursus;
    7: OpretKursusLinjeStep2FormikKursus;
    8: OpretKursusLinjeStep2FormikKursus;
  };
}
interface OpretKursusLinjeStep2FormikKursus {
  navn: string;
  kursuskategori: number | undefined;
  fraDato: Date | undefined;
  fraKlDag: Date | undefined;
  tilKlDag: Date | undefined;
  tilDato: Date | undefined;
}

interface OpretKursusDTO {
  navn: string;
  kursustype: number | undefined;
  hotelId: string | undefined;
  fraDato: string; // "2019-09-09"
  fraKlDag: string; // "10:00:00.0000000"
  tilDato: string; // "2019-09-11"
  tilKlDag: string; // "16:00:00.0000000"
  bemaerkninger: string;
  afbestilGebyr: number;
  refaktureringsgebyr: number;
}
function getOpretKursusDTO(
  values: OpretKursusLinjeStep1Formik,
): OpretKursusDTO {
  return {
    navn: values.navn,
    kursustype: values.kursustype,
    hotelId: values.hotelId,
    fraDato: values.fraDato ? toISOLocal(values.fraDato).split("T")[0] : "",
    fraKlDag: values.fraKlDag
      ? toISOLocal(values.fraKlDag).split("T")[1].split(".")[0]
      : "",
    tilDato: values.tilDato ? toISOLocal(values.tilDato).split("T")[0] : "",
    tilKlDag: values.tilKlDag
      ? toISOLocal(values.tilKlDag).split("T")[1].split(".")[0]
      : "",
    bemaerkninger: values.bemaerkninger,
    afbestilGebyr: values.afbestilGebyr,
    refaktureringsgebyr: values.refaktureringsgebyr,
  };
}

interface OpretLinjeDTO {
  navn: string;
  prisPrDag: number;
  prisPrNat: number;
  hotelId: string;
  kursushold: OpretLinjeKursusDTO[];
}
interface OpretLinjeKursusDTO {
  navn: string;
  kursustype: number | undefined;
  hotelId: string;
  fraDato: string; // "2019-09-09"
  fraKlDag: string; // "10:00:00.0000000"
  tilDato: string; // "2019-09-11"
  tilKlDag: string; // "16:00:00.0000000"
  bemaerkninger: string;
  afbestilGebyr: number;
  refaktureringsgebyr: number;
  fmukursusKategoriNr: number;
}
function getOpretLinjeDTO(
  step1: OpretKursusLinjeStep1Formik,
  step2: OpretKursusLinjeStep2Formik,
): OpretLinjeDTO {
  return {
    navn: step1.navn,
    prisPrDag: 0,
    prisPrNat: 0,
    hotelId: step1.hotelId!,
    kursushold: Object.keys(step2.kurser).map((key) => {
      const kursus = step2.kurser[key] as OpretKursusLinjeStep2FormikKursus;
      const { kursuskategori, fraDato, fraKlDag, tilDato, tilKlDag } = kursus;

      return {
        navn: kursus.navn,
        kursustype: 3,
        hotelId: step1.hotelId!,
        fraDato: format(fraDato!, "yyyy-MM-dd"),
        fraKlDag: format(fraKlDag!, "HH:mm:ss"),
        tilDato: format(tilDato!, "yyyy-MM-dd"),
        tilKlDag: format(tilKlDag!, "HH:mm:ss"),
        bemaerkninger: step1.bemaerkninger,
        afbestilGebyr: 0,
        refaktureringsgebyr: 0,
        fmukursusKategoriNr: kursuskategori!,
      };
    }),
  };
}
